const DISCOUNT_TRIGGER = '[data-fg-discount-code]';
const COPY_TRIGGER = '[data-fg-copy-code]';
const COUNTDOWN_SELECTOR = '[data-fg-offer-countdown][data-countdown-end]';
let discountApplyQueue = Promise.resolve();

function normalizeDiscountCode(value) {
  return String(value || '').trim().toLowerCase();
}

function cartDiscountApplications(cart) {
  const applications = [
    ...(cart.cart_level_discount_applications || []),
    ...(cart.discount_applications || []),
  ];

  (cart.items || []).forEach((item) => {
    const allocations = [
      ...(item.discount_allocations || []),
      ...(item.line_level_discount_allocations || []),
    ];

    allocations.forEach((allocation) => {
      if (allocation.discount_application) applications.push(allocation.discount_application);
    });
  });

  return applications;
}

function appliedDiscountCodes(cart) {
  const codes = new Map();

  cartDiscountApplications(cart).forEach((application) => {
    const type = normalizeDiscountCode(application?.type).replace(/[\s-]+/g, '_');
    const title = String(application?.title || '').trim();

    if (type !== 'discount_code' || !title) return;

    const normalizedTitle = normalizeDiscountCode(title);
    if (!codes.has(normalizedTitle)) codes.set(normalizedTitle, title);
  });

  return [...codes.values()];
}

function mergeDiscountCodes(codes) {
  const uniqueCodes = new Map();

  codes.forEach((code) => {
    const cleanCode = String(code || '').trim();
    const normalizedCode = normalizeDiscountCode(cleanCode);

    if (normalizedCode && !uniqueCodes.has(normalizedCode)) uniqueCodes.set(normalizedCode, cleanCode);
  });

  return [...uniqueCodes.values()];
}

function cartContainsAllDiscountCodes(cart, codes) {
  const activeCodes = new Set(appliedDiscountCodes(cart).map(normalizeDiscountCode));
  return codes.every((code) => activeCodes.has(normalizeDiscountCode(code)));
}

async function fetchCurrentCart() {
  const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    cache: 'no-store',
  });
  const cart = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(cart.description || 'The cart could not be loaded. Please try again.');
  }

  return cart;
}

async function updateCartDiscounts(codes, sections) {
  const body = { discount: codes.join(',') };

  if (sections.length > 0) {
    body.sections = sections;
    body.sections_url = `${window.location.pathname}${window.location.search}`;
  }

  const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  });
  const cart = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(cart.description || 'This discount could not be applied.');
  }

  return cart;
}

function renderCartState(cart, sections) {
  if (!cart) return;

  if (window.themeVariables?.settings?.pageType === 'cart') {
    window.setTimeout(() => window.location.reload(), 250);
    return;
  }

  const sectionsAreReady =
    sections.length > 0 && sections.every((sectionId) => typeof cart.sections?.[sectionId] === 'string');

  if (sectionsAreReady) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'cart:discount',
          cart,
        },
      })
    );
  } else {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }
}

function offerStatus(trigger) {
  const offer = trigger.closest('.fg-cart-offer, .fg-product-offer');
  return offer?.querySelector('.fg-cart-offer__status, .fg-product-offer__status');
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.append(input);
  input.select();

  const copied = document.execCommand('copy');
  input.remove();

  if (!copied) {
    throw new Error('Copy failed');
  }
}

async function copyOfferCode(trigger) {
  const code = trigger.dataset.fgCopyCode?.trim();

  if (!code || trigger.getAttribute('aria-busy') === 'true') {
    return;
  }

  const originalText = trigger.textContent;
  const status = offerStatus(trigger);
  trigger.setAttribute('aria-busy', 'true');

  try {
    await copyText(code);
    trigger.textContent = 'Copied';
    if (status) status.textContent = `Code ${code} copied.`;
  } catch (_error) {
    trigger.textContent = 'Copy again';
    if (status) status.textContent = `Copy failed. Enter ${code} at checkout.`;
  } finally {
    trigger.removeAttribute('aria-busy');
    window.setTimeout(() => {
      if (trigger.isConnected) trigger.textContent = originalText;
    }, 1600);
  }
}

async function applyCartDiscount(trigger) {
  const code = trigger.dataset.fgDiscountCode?.trim();
  const status = offerStatus(trigger);
  let finalCart = null;
  let uniqueSections = [];

  if (!code || trigger.getAttribute('aria-disabled') === 'true' || trigger.getAttribute('aria-busy') === 'true') {
    return;
  }

  trigger.setAttribute('aria-busy', 'true');
  trigger.textContent = 'Applying…';
  status?.classList.remove('is-error');
  if (status) status.textContent = '';

  try {
    const sections = [];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections },
      })
    );

    uniqueSections = [...new Set(sections)];

    const currentCart = await fetchCurrentCart();
    const previousCodes = appliedDiscountCodes(currentCart);
    const requestedCodes = mergeDiscountCodes([...previousCodes, code]);

    if (requestedCodes.length > 5) {
      throw new Error('A maximum of five product or order discount codes can be applied.');
    }

    finalCart = await updateCartDiscounts(requestedCodes, uniqueSections);

    if (!cartContainsAllDiscountCodes(finalCart, requestedCodes)) {
      const previousCodesWereDisplaced = !cartContainsAllDiscountCodes(finalCart, previousCodes);
      let previousCodesRestored = true;

      if (previousCodesWereDisplaced) {
        try {
          finalCart = await updateCartDiscounts(previousCodes, uniqueSections);
          previousCodesRestored = cartContainsAllDiscountCodes(finalCart, previousCodes);
        } catch (_rollbackError) {
          previousCodesRestored = false;
          finalCart = await fetchCurrentCart().catch(() => finalCart);
        }
      }

      const error = new Error(
        previousCodesRestored
          ? 'This offer is not eligible or cannot be combined with the discounts already applied.'
          : 'This offer was not applied, and the previous offers could not be restored automatically. Please refresh the cart.'
      );
      error.cart = finalCart;
      throw error;
    }

    document.querySelectorAll(DISCOUNT_TRIGGER).forEach((candidate) => {
      if (!cartContainsAllDiscountCodes(finalCart, [candidate.dataset.fgDiscountCode])) return;

      candidate.textContent = 'Applied';
      candidate.classList.add('is-applied');
      candidate.setAttribute('aria-disabled', 'true');
      candidate.setAttribute('tabindex', '-1');
    });

    if (status) status.textContent = 'Applied. Estimated total updated.';
    renderCartState(finalCart, uniqueSections);
  } catch (error) {
    trigger.textContent = 'Try again';
    trigger.removeAttribute('tabindex');
    status?.classList.add('is-error');
    if (status) status.textContent = error.message || 'This discount could not be applied.';

    if (error.cart) {
      renderCartState(error.cart, uniqueSections);
    } else if (finalCart) {
      renderCartState(finalCart, uniqueSections);
    } else {
      const currentCart = await fetchCurrentCart().catch(() => null);
      if (currentCart) renderCartState(currentCart, uniqueSections);
    }
  } finally {
    trigger.removeAttribute('aria-busy');
  }
}

function updateOfferCountdowns() {
  document.querySelectorAll(COUNTDOWN_SELECTOR).forEach((countdown) => {
    const value = countdown.querySelector('[data-countdown-value]');
    const rawEnd = (countdown.dataset.countdownEnd || '').trim();
    const hasExplicitTimeZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(rawEnd);
    const endTime = hasExplicitTimeZone ? Date.parse(rawEnd) : Number.NaN;

    if (!value || Number.isNaN(endTime)) {
      countdown.hidden = true;
      return;
    }

    const remaining = endTime - Date.now();

    if (remaining <= 0) {
      const offer = countdown.closest('.fg-product-offer, .fg-cart-offer');
      if (offer) offer.hidden = true;
      else countdown.hidden = true;
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const clock = [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');

    value.textContent = days > 0 ? `${days}d ${clock}` : clock;
    countdown.hidden = false;
  });
}

document.addEventListener('click', (event) => {
  const eventTarget = event.target instanceof Element ? event.target : event.target.parentElement;

  if (!eventTarget) return;

  const discountTrigger = eventTarget.closest(DISCOUNT_TRIGGER);

  if (discountTrigger) {
    event.preventDefault();
    discountApplyQueue = discountApplyQueue
      .catch(() => undefined)
      .then(() => applyCartDiscount(discountTrigger));
    return;
  }

  const copyTrigger = eventTarget.closest(COPY_TRIGGER);

  if (copyTrigger) {
    event.preventDefault();
    copyOfferCode(copyTrigger);
  }
});

if (document.querySelector(COUNTDOWN_SELECTOR)) {
  updateOfferCountdowns();
  const offerCountdownTimer = window.setInterval(updateOfferCountdowns, 1000);
  window.addEventListener('pagehide', () => window.clearInterval(offerCountdownTimer), { once: true });
}
